<template>
    <div class="contact-distributions-by-time" :class="{ fullscreen: fullScreen }">
        <div class="title" v-if="fullScreen">
            <ChartDoddleIcon />
            <h2>{{ title }}</h2>
        </div>

        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('report.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('report.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('report.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                    <IconButton :onClick="switchToFullScreenMode" v-tippy="{ content: $t('report.fullScreen') }">
                        <md-icon v-if="!fullScreen">fullscreen</md-icon>
                        <md-icon v-else>fullscreen_exit</md-icon>
                    </IconButton>
                </div>
            </div>
        </div>

        <a-card :loading="loading" :bordered="false" class="chart-card">
            <a-empty v-if="!loading && (!data || !data.buckets || data.buckets.length === 0)" />
            <template v-else>
                <div v-if="!tableMode" class="chart-wrapper">
                    <StackedAreaChart v-bind="chartData" />
                </div>
                <div v-else class="table-wrapper">
                    <TableView :cols="getCols()" :rows="getRows()" />
                </div>
            </template>
        </a-card>

        <div class="stats">
            <StatCard
                :title="$t('report.total')"
                :value="formatStat(stats.total).value"
                :unit="$t('report.person')"
            />
            <StatCard
                :title="getTitle('average')"
                :value="formatStat(stats.average).value"
                :unit="$t('report.person')"
            />
            <StatCard
                :title="getTitle('max')"
                :value="formatStat(stats.max).value"
                :unit="$t('report.person')"
            />
            <StatCard
                :title="getTitle('min')"
                :value="stats.min"
                :unit="$t('report.person')"
            />
        </div>
    </div>
</template>

<script>
import moment from "moment";
import _ from "lodash";
import log2 from "../../../utils/log2";
import TimeUtils from "../../../utils/TimeUtils";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import ChartDoddleIcon from "../common/icons/ChartDoddleIcon.vue";
import fscreen from "fscreen";
import { expertToExcel } from "../../../utils/utils";
import StackedAreaChart from "../../Pages/Chatbot/pages/reports/charts/StackedAreaChart.vue";
import * as echarts from "echarts/lib/echarts";
const log = log2("ContactDistributionsByTime");

export default {
    name: "ContactDistributionsByTime",
    components: {
        IconButton,
        StatCard,
        TableView,
        ChartDoddleIcon,
        StackedAreaChart
    },
    props: {
        title: {
            type: String,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        data: {
            type: Object,
            default: () => ({
                buckets: [],
                timeSeriesType: null,
            }),
        },
    },
    data() {
        return {
            chartType: "line",
            tableMode: false,
            fullScreen: false,
            cancels: [],
            stats: {
                total: 0,
                average: 0,
                max: 0,
                min: 0,
                isHourlyAverage: false
            }
        };
    },
    computed: {
        chartData() {
            if (!this.data || !this.data.buckets) return {};

            return {
                title: this.title,
                legends: ["Kişi Sayısı"],
                xAxisData: this.data.buckets.map((item) => {
                    const date = moment(item.date);
                    if (this.data.timeSeriesType === "hour") {
                        return date.format("HH:mm");
                    } else if (this.data.timeSeriesType === "day") {
                        return date.format("DD MMM");
                    } else if (this.data.timeSeriesType === "month") {
                        return date.format("MMM YYYY");
                    }
                    return date.format("DD MMM YYYY");
                }),
                series: [
                    {
                        name: this.$t("report.personCount"),
                        type: "line",
                        stack: "Total",
                        smooth: true,
                        lineStyle: {
                            width: 0
                        },
                        showSymbol: false,
                        areaStyle: {
                            opacity: 0.8,
                            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                {
                                    offset: 0,
                                    color: 'rgb(55, 162, 255)'
                                },
                                {
                                    offset: 1,
                                    color: 'rgb(116, 21, 219)'
                                }
                            ])
                        },
                        emphasis: {
                            focus: 'series'
                        },
                        data: this.data.buckets.map((item) => item.count)
                    }
                ],
                colors: ['rgb(55, 162, 255)']
            };
        }
    },
    watch: {
        data: {
            immediate: true,
            handler(newData) {
                if (newData && newData.buckets && newData.buckets.length > 0) {
                    const counts = newData.buckets.map(item => item.count);

                    // Toplam hesaplama
                    this.stats.total = counts.reduce((sum, count) => sum + count, 0);

                    // Maksimum değer
                    this.stats.max = Math.max(...counts);

                    // Minimum değer
                    this.stats.min = Math.min(...counts);

                    // Ortalama hesaplama
                    this.stats.average = Math.round(this.stats.total / counts.length);

                    // Saatlik ortalama kontrolü
                    this.stats.isHourlyAverage = newData.timeSeriesType === 'hour';

                    // Eğer saatlik veri ise ve 24 saatten fazla veri varsa, günlük ortalamaya çevir
                    if (this.stats.isHourlyAverage && counts.length > 24) {
                        const totalDays = Math.ceil(counts.length / 24);
                        this.stats.average = Math.round(this.stats.total / totalDays);
                    }
                } else {
                    // Veri yoksa stats'ları sıfırla
                    this.stats = {
                        total: 0,
                        average: 0,
                        max: 0,
                        min: 0,
                        isHourlyAverage: false
                    };
                }
            }
        }
    },
    methods: {
        switchToFullScreenMode() {
            if (this.fullScreen) {
                this.closeFullScreenMode();
            } else {
                const elem = document.querySelector(".contact-distributions-by-time");
                fscreen.requestFullscreen(elem);
                this.fullScreen = true;
            }
        },
        closeFullScreenMode() {
            fscreen.exitFullscreen();
        },
        getCols() {
            return [
                {
                    title: this.$t("chatbotv2.date"),
                    dataIndex: "date",
                    key: "date",
                },
                {
                    title: this.$t("report.contactCount"),
                    dataIndex: "count",
                    key: "count",
                }
            ];
        },
        getRows() {
            return this.data.buckets.map(bucket => ({
                date: this.formatDate(bucket.date),
                count: bucket.count
            }));
        },
        exportToExcel() {
            const data = this.getRows();
            expertToExcel(data, `contact_distributions_${moment().format("YYYY-MM-DD")}.xlsx`, "Sheet 1");
        },
        formatStat(value) {
            if (typeof value !== 'number' || isNaN(value)) {
                return {
                    value: this.$t("report.noEnoughtData"),
                    unit: ""
                };
            }
            return {
                value: value,
                unit: this.$t("report.contact")
            };
        },
        getTitle(type) {
            const hourlyText = this.$t("report.hourly");
            if (this.stats.isHourlyAverage && type === 'average') {
                return `${this.$t(`report.${type}`)} ${hourlyText}`;
            }
            return this.$t(`report.${type}`);
        },
        formatDate(date) {
            const momentDate = moment(date);
            if (this.data.timeSeriesType === "hour") {
                return momentDate.format("DD MMM HH:mm");
            } else if (this.data.timeSeriesType === "day") {
                return momentDate.format("DD MMM YYYY");
            } else if (this.data.timeSeriesType === "month") {
                return momentDate.format("MMM YYYY");
            }
            return momentDate.format("DD MMM YYYY");
        }
    },
    created() {
        const fullscreenCallback = () => {
            this.fullScreen = fscreen.fullscreenElement !== null;
            this.$forceUpdate();
        };
        fscreen.addEventListener("fullscreenchange", fullscreenCallback);
        this.cancels.push(() => {
            fscreen.removeEventListener("fullscreenchange", fullscreenCallback);
        });
    },
    beforeDestroy() {
        this.cancels.forEach(cancel => cancel());
    }
};
</script>

<style lang="scss">
.contact-distributions-by-time {
    .title {
        display: flex;
        gap: 4px;
        align-items: center;
        justify-content: center;
        margin: 0;

        h2 {
            text-align: center;
            font-weight: 700;
            font-size: 40px;
            margin: 0 !important;
        }
    }

    &.fullscreen {
        background-color: #fff;
        padding: 20px;
        overflow-y: scroll;

        .chart-wrapper {
            height: calc(100vh - 200px);
        }
    }
}
</style>

<style lang="scss" scoped>
.contact-distributions-by-time {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }

    .chart-card {
        width: 100%;
    }

    .chart-wrapper, .table-wrapper {
        width: 100%;
        height: 400px;
    }

    .table-wrapper {
        overflow: auto;
    }

    &.fullscreen {
        .chart-wrapper, .table-wrapper {
            height: calc(100vh - 200px);
        }
    }
}
</style>
